import React, { useState, useEffect } from 'react';
import { FileText, Download, Calendar, ShieldAlert, BookOpen, Image as ImageIcon, Loader2 } from 'lucide-react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const ReportsDashboard = ({ inline = false }) => {
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return d.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [stats, setStats] = useState({ incidents: [], logs: [], photos: [] });
    const [includeEhs, setIncludeEhs] = useState(true);
    const [includeLogs, setIncludeLogs] = useState(true);
    const [includePhotos, setIncludePhotos] = useState(true);

    const activeProjectName = localStorage.getItem('activeProjectName') || 'Proyecto Sin Asignar';
    const activeProjectId = localStorage.getItem('activeProjectId');
    const [logoUrl, setLogoUrl] = useState(null);

    useEffect(() => {
        const fetchProjectLogo = async () => {
            const token = localStorage.getItem('token');
            if (!token || !activeProjectId) return;
            try {
                const res = await axios.get('http://localhost:4000/api/projects', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const activeProj = res.data.find(p => p.id === parseInt(activeProjectId));
                if (activeProj && activeProj.logo_url) {
                    setLogoUrl(activeProj.logo_url);
                }
            } catch (err) {
                console.error("Error al obtener logotipo del proyecto en ReportsDashboard:", err);
            }
        };
        fetchProjectLogo();
    }, [activeProjectId]);

    const fetchData = async () => {
        if (!activeProjectId) return;
        try {
            setLoading(true);
            const headers = {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'x-project-id': activeProjectId
            };
            const [incidentsRes, logsRes, photosRes] = await Promise.all([
                axios.get('http://localhost:4000/api/incidents', { headers }),
                axios.get('http://localhost:4000/api/logs', { headers }),
                axios.get('http://localhost:4000/api/photos', { headers })
            ]);

            // Filter data by date range
            const start = new Date(startDate + 'T00:00:00');
            const end = new Date(endDate + 'T23:59:59');

            const filteredIncidents = (incidentsRes.data || []).filter(item => {
                const itemDate = new Date(item.created_at || item.timestamp);
                return itemDate >= start && itemDate <= end;
            });

            const filteredLogs = (logsRes.data || []).filter(item => {
                const itemDate = new Date(item.log_date || item.created_at);
                return itemDate >= start && itemDate <= end;
            });

            const filteredPhotos = (photosRes.data || []).filter(item => {
                const itemDate = new Date(item.captured_at);
                return itemDate >= start && itemDate <= end;
            });

            setStats({
                incidents: filteredIncidents,
                logs: filteredLogs,
                photos: filteredPhotos
            });
        } catch (error) {
            console.error("Error al cargar datos de reportes:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [startDate, endDate, activeProjectId]);

    // Convert image URL to base64 for PDF generation
    const getBase64ImageFromUrl = async (imageUrl) => {
        try {
            const res = await fetch(imageUrl);
            const blob = await res.blob();
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch (err) {
            console.error("Error converting image to base64:", err);
            return null;
        }
    };

    const handleGenerateReport = async () => {
        try {
            setGenerating(true);
            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();

            // Pre-cargar logotipo
            let base64Logo = null;
            let logoFormat = 'PNG';
            if (logoUrl) {
                try {
                    base64Logo = await getBase64ImageFromUrl(logoUrl);
                    logoFormat = logoUrl.endsWith('.jpg') || logoUrl.endsWith('.jpeg') ? 'JPEG' : 'PNG';
                } catch (logoErr) {
                    console.error("Error pre-loading logo for PDF:", logoErr);
                }
            }

            // --- PAGE 1: COVER PAGE ---
            // Draw background tech bands
            doc.setFillColor(26, 42, 60); // Navy background header
            doc.rect(0, 0, pageWidth, 80, 'F');
            doc.setFillColor(190, 161, 109); // Gold stripe
            doc.rect(0, 80, pageWidth, 5, 'F');

            // Cover Title
            doc.setTextColor(255, 255, 255);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(28);
            doc.text("ARCHI 24/7", 20, 45);
            doc.setFontSize(14);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(190, 161, 109);
            doc.text("CONTECH OPERATIONS INTELLIGENCE PLATFORM", 20, 57);

            // Draw logo on cover
            if (base64Logo) {
                doc.addImage(base64Logo, logoFormat, pageWidth - 65, 20, 45, 20);
            }

            // Document Details
            doc.setTextColor(51, 65, 85); // Slate 700
            doc.setFontSize(22);
            doc.setFont("helvetica", "bold");
            doc.text("REPORTE EJECUTIVO DE OBRA", 20, 120);

            doc.setFontSize(12);
            doc.setFont("helvetica", "normal");
            doc.text(`Proyecto: ${activeProjectName}`, 20, 135);
            doc.text(`Periodo del Reporte: ${new Date(startDate).toLocaleDateString()} al ${new Date(endDate).toLocaleDateString()}`, 20, 145);
            doc.text(`Fecha de Emisión: ${new Date().toLocaleDateString()}`, 20, 155);

            // Statistics Overview on Cover
            doc.setFillColor(248, 250, 252); // Soft slate box
            doc.rect(20, 180, pageWidth - 40, 70, 'F');
            doc.setDrawColor(226, 232, 240);
            doc.rect(20, 180, pageWidth - 40, 70, 'S');

            doc.setFont("helvetica", "bold");
            doc.setFontSize(14);
            doc.text("RESUMEN OPERATIVO DEL PERIODO", 30, 200);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(11);
            doc.text(`- Alarmas de Seguridad Detectadas: ${stats.incidents.length}`, 30, 215);
            doc.text(`- Registros Certificados en Bitácora: ${stats.logs.length}`, 30, 225);
            doc.text(`- Capturas y Avances Visuales Guardados: ${stats.photos.length}`, 30, 235);

            // Footer
            doc.setTextColor(148, 163, 184);
            doc.setFontSize(9);
            doc.text("Confidencial - Para uso interno exclusivo de administración de obra.", pageWidth / 2, pageHeight - 15, { align: 'center' });

            // --- PAGE 2: SEGURIDAD EHS ---
            if (includeEhs) {
                doc.addPage();
                doc.setFillColor(26, 42, 60);
                doc.rect(0, 0, pageWidth, 20, 'F');
                doc.setTextColor(255, 255, 255);
                doc.setFont("helvetica", "bold");
                doc.setFontSize(12);
                doc.text("AUDITORÍA DE SEGURIDAD EHS", 15, 13);

                if (base64Logo) {
                    doc.addImage(base64Logo, logoFormat, pageWidth - 45, 3, 30, 14);
                }

                doc.setTextColor(51, 65, 85);
                doc.setFontSize(16);
                doc.text("Alertas e Incidentes de Seguridad Activos", 15, 35);
                doc.setFontSize(10);
                doc.setFont("helvetica", "normal");
                doc.text("Operarios ingresando a zonas de riesgo sin equipo de seguridad auditados por Vision AI:", 15, 42);

                if (stats.incidents.length === 0) {
                    doc.setFont("helvetica", "italic");
                    doc.text("No se registraron incidentes de seguridad en el periodo seleccionado.", 15, 55);
                } else {
                    const rows = stats.incidents.map(item => [
                        item.incident_type,
                        `Cámara ${item.camera_id}`,
                        `${Math.round((item.confidence_score || 0) * 100)}%`,
                        new Date(item.created_at).toLocaleString(),
                        item.is_resolved ? 'Controlado' : 'Alerta Activa'
                    ]);

                    autoTable(doc, {
                        startY: 50,
                        head: [['Tipo de Incidente', 'Origen', 'Confianza', 'Fecha y Hora', 'Estado']],
                        body: rows,
                        headStyles: { fillColor: [26, 42, 60], textColor: [255, 255, 255] },
                        alternateRowStyles: { fillColor: [248, 250, 252] }
                    });
                }
            }

            // --- PAGE 3: BITÁCORA ---
            if (includeLogs) {
                doc.addPage();
                doc.setFillColor(26, 42, 60);
                doc.rect(0, 0, pageWidth, 20, 'F');
                doc.setTextColor(255, 255, 255);
                doc.setFont("helvetica", "bold");
                doc.setFontSize(12);
                doc.text("BITÁCORA OPERATIVA DE OBRA", 15, 13);

                if (base64Logo) {
                    doc.addImage(base64Logo, logoFormat, pageWidth - 45, 3, 30, 14);
                }

                doc.setTextColor(51, 65, 85);
                doc.setFontSize(16);
                doc.text("Registros de Progreso Certificados", 15, 35);
                doc.setFontSize(10);
                doc.setFont("helvetica", "normal");
                doc.text("Log diario de actividades de cuadrillas y eventos relevantes en el periodo:", 15, 42);

                if (stats.logs.length === 0) {
                    doc.setFont("helvetica", "italic");
                    doc.text("No se encontraron registros de bitácora en el periodo seleccionado.", 15, 55);
                } else {
                    const rows = stats.logs.map(item => [
                        new Date(item.log_date).toLocaleDateString(),
                        item.title,
                        item.description,
                        item.created_by_name || 'Supervisor'
                    ]);

                    autoTable(doc, {
                        startY: 50,
                        head: [['Fecha', 'Título del Evento', 'Detalles', 'Registrado Por']],
                        body: rows,
                        headStyles: { fillColor: [26, 42, 60], textColor: [255, 255, 255] },
                        alternateRowStyles: { fillColor: [248, 250, 252] }
                    });
                }
            }

            // --- PAGE 4: REGISTRO FOTOGRÁFICO ---
            if (includePhotos && stats.photos.length > 0) {
                doc.addPage();
                doc.setFillColor(26, 42, 60);
                doc.rect(0, 0, pageWidth, 20, 'F');
                doc.setTextColor(255, 255, 255);
                doc.setFont("helvetica", "bold");
                doc.setFontSize(12);
                doc.text("REGISTRO FOTOGRÁFICO DE AVANCE", 15, 13);

                if (base64Logo) {
                    doc.addImage(base64Logo, logoFormat, pageWidth - 45, 3, 30, 14);
                }

                doc.setTextColor(51, 65, 85);
                doc.setFontSize(16);
                doc.text("Galería Visual del Sitio de Trabajo", 15, 35);

                // Add up to 4 photos to keep page size reasonable
                const photosToRender = stats.photos.slice(0, 4);
                let currentY = 45;

                for (let i = 0; i < photosToRender.length; i++) {
                    const photo = photosToRender[i];
                    const base64 = await getBase64ImageFromUrl(photo.url);

                    if (base64) {
                        // Draw two images per page
                        if (i > 0 && i % 2 === 0) {
                            doc.addPage();
                            doc.setFillColor(26, 42, 60);
                            doc.rect(0, 0, pageWidth, 20, 'F');
                            doc.setTextColor(255, 255, 255);
                            doc.setFont("helvetica", "bold");
                            doc.setFontSize(12);
                            doc.text("REGISTRO FOTOGRÁFICO DE AVANCE (CONT.)", 15, 13);

                            if (base64Logo) {
                                doc.addImage(base64Logo, logoFormat, pageWidth - 45, 3, 30, 14);
                            }

                            currentY = 35;
                        }

                        doc.addImage(base64, 'JPEG', 15, currentY, 110, 75);
                        
                        doc.setTextColor(51, 65, 85);
                        doc.setFont("helvetica", "bold");
                        doc.setFontSize(11);
                        doc.text(`Captura #${i + 1}`, 135, currentY + 15);
                        doc.setFont("helvetica", "normal");
                        doc.setFontSize(9);
                        doc.text(`Fecha: ${new Date(photo.capturedAt || photo.date).toLocaleString()}`, 135, currentY + 25);
                        doc.text(`Operarios en Escena: ${photo.personCount}`, 135, currentY + 32);

                        currentY += 85;
                    }
                }
            }

            doc.save(`Reporte_Ejecutivo_${activeProjectName.replace(/\s+/g, '_')}_${startDate}.pdf`);
        } catch (error) {
            console.error("Error generating PDF:", error);
            alert("Ocurrió un error al generar el PDF. Por favor, revisa la consola.");
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div className={inline ? "text-theme-text-primary font-sans" : "min-h-screen bg-theme-bg bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(37,99,235,0.15),rgba(255,255,255,0))] text-theme-text-primary p-4 md:p-8 font-sans"}>
            <div className={inline ? "w-full" : "max-w-4xl mx-auto"}>
                {/* Header */}
                {!inline && (
                    <div className="mb-10">
                        <h1 className="text-4xl font-extrabold tracking-tight text-theme-text-primary mb-2 flex items-center gap-3">
                            <FileText className="text-blue-500" size={36} /> Centro de Reportes Ejecutivos
                        </h1>
                        <p className="text-theme-text-secondary font-medium">
                            Genera informes corporativos en PDF con telemetría visual, bitácoras de obra y auditorías de seguridad.
                        </p>
                    </div>
                )}

                {/* Main Settings Card */}
                <div className={inline ? "bg-theme-panel p-6 rounded-2xl border border-theme-border shadow-lg backdrop-blur-xl mb-8" : "bg-theme-panel p-6 md:p-8 rounded-3xl border border-theme-border shadow-2xl backdrop-blur-xl mb-8"}>
                    {!inline && <h3 className="text-xl font-bold text-theme-text-primary mb-6">Parámetros del Reporte</h3>}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        {/* Start Date */}
                        <div className="flex flex-col">
                            <label className="text-xs font-bold text-theme-text-secondary uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <Calendar size={14} className="text-blue-400" /> Fecha de Inicio
                            </label>
                            <input 
                                type="date" 
                                value={startDate} 
                                onChange={(e) => setStartDate(e.target.value)} 
                                className="w-full bg-theme-bg border border-theme-border text-theme-text-primary px-4 py-3 rounded-xl focus:outline-none focus:border-blue-500 transition-colors font-semibold"
                            />
                        </div>

                        {/* End Date */}
                        <div className="flex flex-col">
                            <label className="text-xs font-bold text-theme-text-secondary uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <Calendar size={14} className="text-blue-400" /> Fecha de Finalización
                            </label>
                            <input 
                                type="date" 
                                value={endDate} 
                                onChange={(e) => setEndDate(e.target.value)} 
                                className="w-full bg-theme-bg border border-theme-border text-theme-text-primary px-4 py-3 rounded-xl focus:outline-none focus:border-blue-500 transition-colors font-semibold"
                            />
                        </div>
                    </div>

                    {/* Section Selector Checks */}
                    <div className="border-t border-theme-border pt-6 mb-8">
                        <h4 className="text-sm font-bold text-theme-text-secondary uppercase tracking-wider mb-4">Secciones a Incluir</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <label className="flex items-center gap-3 bg-theme-bg p-4 rounded-2xl border border-theme-border cursor-pointer hover:bg-theme-border/20 transition-all">
                                <input 
                                    type="checkbox" 
                                    checked={includeEhs} 
                                    onChange={(e) => setIncludeEhs(e.target.checked)} 
                                    className="h-5 w-5 rounded text-blue-600 focus:ring-blue-500" 
                                />
                                <div>
                                    <p className="font-bold text-sm text-theme-text-primary flex items-center gap-1.5">
                                        <ShieldAlert size={16} className="text-red-400" /> Seguridad EHS
                                    </p>
                                    <p className="text-[10px] text-theme-text-secondary mt-0.5">{stats.incidents.length} Incidentes en rango</p>
                                </div>
                            </label>

                            <label className="flex items-center gap-3 bg-theme-bg p-4 rounded-2xl border border-theme-border cursor-pointer hover:bg-theme-border/20 transition-all">
                                <input 
                                    type="checkbox" 
                                    checked={includeLogs} 
                                    onChange={(e) => setIncludeLogs(e.target.checked)} 
                                    className="h-5 w-5 rounded text-blue-600 focus:ring-blue-500" 
                                />
                                <div>
                                    <p className="font-bold text-sm text-theme-text-primary flex items-center gap-1.5">
                                        <BookOpen size={16} className="text-blue-400" /> Bitácora
                                    </p>
                                    <p className="text-[10px] text-theme-text-secondary mt-0.5">{stats.logs.length} Registros en rango</p>
                                </div>
                            </label>

                            <label className="flex items-center gap-3 bg-theme-bg p-4 rounded-2xl border border-theme-border cursor-pointer hover:bg-theme-border/20 transition-all">
                                <input 
                                    type="checkbox" 
                                    checked={includePhotos} 
                                    onChange={(e) => setIncludePhotos(e.target.checked)} 
                                    className="h-5 w-5 rounded text-blue-600 focus:ring-blue-500" 
                                />
                                <div>
                                    <p className="font-bold text-sm text-theme-text-primary flex items-center gap-1.5">
                                        <ImageIcon size={16} className="text-green-400" /> Fotos y Avances
                                    </p>
                                    <p className="text-[10px] text-theme-text-secondary mt-0.5">{stats.photos.length} Fotos en rango</p>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Download Button */}
                    <button 
                        onClick={handleGenerateReport} 
                        disabled={loading || generating || (!includeEhs && !includeLogs && !includePhotos)}
                        className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-bold py-4 rounded-xl transition-all shadow-xl shadow-blue-500/20 hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2.5 text-base"
                    >
                        {generating ? (
                            <>
                                <Loader2 className="animate-spin" size={20} />
                                Compilando Documento PDF...
                            </>
                        ) : (
                            <>
                                <Download size={20} />
                                Generar e Imprimir Reporte Corporativo
                            </>
                        )}
                    </button>
                </div>

                {/* Preview Card */}
                {loading ? (
                    <div className="bg-theme-panel/40 border border-theme-border rounded-3xl p-10 text-center">
                        <Loader2 className="animate-spin text-blue-500 mx-auto mb-4" size={32} />
                        <p className="text-theme-text-secondary font-medium">Actualizando telemetría del periodo...</p>
                    </div>
                ) : (
                    <div className="bg-theme-panel/30 border border-theme-border rounded-3xl p-6 md:p-8 backdrop-blur-md">
                        <h4 className="font-bold text-sm text-theme-text-secondary uppercase tracking-wider mb-4 border-b border-theme-border pb-2">Vista Previa de Telemetría (Obra: {activeProjectName})</h4>
                        <div className="grid grid-cols-3 gap-4 text-center">
                            <div className="bg-theme-panel/60 p-4 rounded-2xl border border-theme-border">
                                <span className="text-2xl md:text-3xl font-black text-red-400">{stats.incidents.length}</span>
                                <p className="text-[10px] md:text-xs font-bold text-theme-text-secondary uppercase tracking-wider mt-1">Alertas EHS</p>
                            </div>
                            <div className="bg-theme-panel/60 p-4 rounded-2xl border border-theme-border">
                                <span className="text-2xl md:text-3xl font-black text-blue-400">{stats.logs.length}</span>
                                <p className="text-[10px] md:text-xs font-bold text-theme-text-secondary uppercase tracking-wider mt-1">Bitácora Logs</p>
                            </div>
                            <div className="bg-theme-panel/60 p-4 rounded-2xl border border-theme-border">
                                <span className="text-2xl md:text-3xl font-black text-green-400">{stats.photos.length}</span>
                                <p className="text-[10px] md:text-xs font-bold text-theme-text-secondary uppercase tracking-wider mt-1">Fotos Sitio</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReportsDashboard;
